// A site id becomes a Firestore document path (sites/<id>/...), so it must be
// a single path segment with no metacharacters. This is the shape the signup
// UI produces and the `SiteId` request schema (http/common.ts) enforces at the
// HTTP boundary; verifying it here as well backstops the non-HTTP callers (CLI,
// triggers, scheduled jobs) and any path built without going through a route.
const SITE_ID_PATTERN = /^[a-z0-9-]{3,30}$/;
const verifySiteId = (siteId) => {
    if (!siteId || !SITE_ID_PATTERN.test(siteId)) {
        throw new Error('Invalid siteId');
    }
};
const verifyYearMonth = (yearMonth) => {
    const re = /^\d{4}-\d{1,2}$/;
    if (!re.test(yearMonth)) {
        throw new Error('Invalid yearMonth format');
    }
};
const verifyUid = (uid) => {
    // FB auto-generated uid is 28 chars but custom can be from 1 to 128
    // This is just to avoid silly mistakes and not a security measure
    if (!uid || uid.length < 6) {
        throw new Error('Invalid uid');
    }
};
const FBPATHS = {
    subscriptions: {
        doc: (uid) => {
            verifyUid(uid);
            return `subscriptions/${uid}`;
        },
        collection: 'subscriptions'
    },
    platformmail: {
        doc: (mailId) => `platformmail/${mailId}`,
        collection: 'platformmail'
    },
    admins: {
        doc: (uid) => {
            verifyUid(uid);
            return `admins/${uid}`;
        },
        collection: 'admins'
    },
    // Platform-wide booking activity, one doc per UTC day (yyyy-MM-dd).
    // Written by the nightly job, read by the global admin via functions.
    dailyActivity: {
        doc: (date) => {
            if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
                throw new Error('Invalid date format');
            }
            return `dailyActivity/${date}`;
        },
        collection: 'dailyActivity'
    },
    // One doc per release name, written by the deploy pipeline (cloudbuild.yaml)
    // with a server timestamp per deployed target.
    deploys: {
        doc: (release) => `deploys/${release}`,
        collection: 'deploys'
    },
    config: {
        stripe: 'config/stripe',
        domains: 'config/domains',
        mail: 'config/mail',
        auth: 'config/auth'
    },
    users: {
        doc: (uid) => {
            verifyUid(uid);
            return `users/${uid}`;
        },
        collection: 'users'
    },
    sites: {
        doc: (siteId) => {
            verifySiteId(siteId);
            return `sites/${siteId}`;
        },
        collection: 'sites',
        shadow: {
            collectionGroup: 'shadow',
            doc: (siteId) => `sites/${siteId}/shadow/site`
        },
        shadowUsers: {
            doc: (siteId, uid) => `sites/${siteId}/shadowUsers/${uid}`,
            collection: (siteId) => `sites/${siteId}/shadowUsers`
        },
        orders: {
            collectionGroup: 'orders',
            doc: (siteId, orderId) => `sites/${siteId}/orders/${orderId}`,
            collection: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/orders`;
            }
        },
        orderLocks: {
            doc: (siteId, orderId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/orderLocks/${orderId}`;
            },
            collection: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/orderLocks`;
            }
        },
        usage: {
            doc: (siteId, yyyyMM) => {
                verifySiteId(siteId);
                verifyYearMonth(yyyyMM);
                return `sites/${siteId}/usage/${yyyyMM}`;
            },
            collection: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/usage`;
            }
        },
        hooklog: {
            doc: (siteId, hookId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/hooklog/${hookId}`;
            },
            collection: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/hooklog`;
            }
        },
        coupons: {
            doc: (siteId, couponId) => {
                return `sites/${siteId}/coupons/${couponId}`;
            },
            collection: (siteId) => {
                return `sites/${siteId}/coupons`;
            }
        },
        invoices: {
            doc: (siteId, invoiceId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/invoices/${invoiceId}`;
            },
            collection: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/invoices`;
            }
        },
        users: {
            doc: (siteId, uid) => {
                verifySiteId(siteId);
                verifyUid(uid);
                return `sites/${siteId}/users/${uid}`;
            },
            collection: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/users`;
            },
            calendar: {
                doc: (siteId, uid, yearMonth) => {
                    verifySiteId(siteId);
                    verifyUid(uid);
                    verifyYearMonth(yearMonth);
                    return `sites/${siteId}/users/${uid}/calendar/${yearMonth}`;
                },
                collection: (siteId, uid) => {
                    verifySiteId(siteId);
                    verifyUid(uid);
                    return `sites/${siteId}/users/${uid}/calendar`;
                }
            },
            carts: {
                doc: (siteId, uid, cartId) => {
                    verifySiteId(siteId);
                    verifyUid(uid);
                    return `sites/${siteId}/users/${uid}/carts/${cartId}`;
                },
                collection: (siteId, uid) => {
                    verifySiteId(siteId);
                    verifyUid(uid);
                    return `sites/${siteId}/users/${uid}/carts`;
                }
            },
            orders: {
                doc: (siteId, uid, orderId) => {
                    verifySiteId(siteId);
                    verifyUid(uid);
                    return `sites/${siteId}/users/${uid}/orders/${orderId}`;
                },
                collection: (siteId, uid) => {
                    verifySiteId(siteId);
                    verifyUid(uid);
                    return `sites/${siteId}/users/${uid}/orders`;
                }
            },
            events: {
                doc: (siteId, uid, eventId) => {
                    verifySiteId(siteId);
                    verifyUid(uid);
                    return `sites/${siteId}/users/${uid}/events/${eventId}`;
                },
                collection: (siteId, uid) => {
                    verifySiteId(siteId);
                    verifyUid(uid);
                    return `sites/${siteId}/users/${uid}/events`;
                },
                handlerforms: {
                    doc: (siteId, uid, eventId, formId) => {
                        verifySiteId(siteId);
                        return `sites/${siteId}/users/${uid}/events/${eventId}/handlerforms/${formId}`;
                    },
                    collection: (siteId, uid, eventId) => {
                        return `sites/${siteId}/users/${uid}/events/${eventId}/handlerforms`;
                    }
                },
                dogforms: {
                    doc: (siteId, uid, eventId, formId) => {
                        verifySiteId(siteId);
                        return `sites/${siteId}/users/${uid}/events/${eventId}/dogforms/${formId}`;
                    },
                    collection: (siteId, uid, eventId) => {
                        return `sites/${siteId}/users/${uid}/events/${eventId}/dogforms`;
                    }
                },
                equipageforms: {
                    doc: (siteId, uid, eventId, formId) => {
                        verifySiteId(siteId);
                        return `sites/${siteId}/users/${uid}/events/${eventId}/equipageforms/${formId}`;
                    },
                    collection: (siteId, uid, eventId) => {
                        return `sites/${siteId}/users/${uid}/events/${eventId}/equipageforms`;
                    }
                },
                regs: {
                    doc: (siteId, uid, eventId, subId) => {
                        verifySiteId(siteId);
                        return `sites/${siteId}/users/${uid}/events/${eventId}/regs/${subId}`;
                    },
                    collection: (siteId, uid, eventId) => {
                        verifySiteId(siteId);
                        return `sites/${siteId}/users/${uid}/events/${eventId}/regs`;
                    }
                }
            },
            punchcards: {
                doc: (siteId, uid, cardId) => {
                    verifySiteId(siteId);
                    verifyUid(uid);
                    return `sites/${siteId}/users/${uid}/punchcards/${cardId}`;
                },
                collection: (siteId, uid) => {
                    verifySiteId(siteId);
                    verifyUid(uid);
                    return `sites/${siteId}/users/${uid}/punchcards`;
                }
            },
            invoices: {
                doc: (siteId, uid, invoiceId) => {
                    verifySiteId(siteId);
                    verifyUid(uid);
                    return `sites/${siteId}/users/${uid}/invoices/${invoiceId}`;
                },
                collection: (siteId, uid) => {
                    verifySiteId(siteId);
                    verifyUid(uid);
                    return `sites/${siteId}/users/${uid}/invoices`;
                }
            }
        },
        admin: {
            doc: (siteId, uid) => {
                verifySiteId(siteId);
                verifyUid(uid);
                return `sites/${siteId}/admin/${uid}`;
            },
            collection: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/admin`;
            }
        },
        admincal: {
            doc: (siteId, yyyyMM) => {
                verifySiteId(siteId);
                verifyYearMonth(yyyyMM);
                return `sites/${siteId}/admincal/${yyyyMM}`;
            },
            collection: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/admincal`;
            }
        },
        calendar: {
            doc: (siteId, yyyyMM) => {
                verifySiteId(siteId);
                verifyYearMonth(yyyyMM);
                return `sites/${siteId}/calendar/${yyyyMM}`;
            },
            collection: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/calendar`;
            }
        },
        config: {
            codes: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/config/codes`;
            },
            // Join codes and pending invites. Group *definitions* are site data and
            // live in the site document; only the secrets are here. docs/plans/groups.md.
            groups: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/config/groups`;
            },
            order: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/config/order`;
            },
            paypal: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/config/paypal`;
            },
            stripe: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/config/stripe`;
            },
            vipps: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/config/vipps`;
            },
            vippsCheckout: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/config/vippsCheckout`;
            },
            account: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/config/account`;
            },
            event: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/config/event`;
            },
            incident: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/config/incident`;
            },
            invoice: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/config/invoice`;
            },
            invoiceCounter: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/config/invoiceCounter`;
            },
            lights: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/config/lights`;
            },
            // Device check-ins, apart from config/lights so a poll never fires the
            // admin page's snapshot of the document being edited (D74).
            lightsStatus: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/config/lightsStatus`;
            }
        },
        /**
         * Server-only. Firestore rules deny all client access to sites/{id}/private/**.
         * Device credentials live here, never in the site doc (world-readable) nor
         * in config/* (admin-readable). See docs/plans/light_control.md.
         */
        private: {
            // Nested under a fixed `lights` container document so both the
            // collection and the per-device document land on valid Firestore paths
            // (a document needs an even number of segments; .../private/devices/{id}
            // is odd and Firestore rejects it). The `lights` layer also keeps device
            // credentials from mixing with other future sites/{id}/private/* secrets.
            device: (siteId, deviceId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/private/lights/devices/${deviceId}`;
            },
            devices: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/private/lights/devices`;
            },
            // Per-order webhook callback token (F-PAY-02). Deliberately NOT on the
            // order document: ledger.logic.book copies the whole order into the
            // user-readable mirror at sites/{id}/users/{uid}/orders/{id}, so a secret
            // living in order.meta would have to be stripped at every mirroring site
            // and would stay readable by site admins. Here it is unreachable by every
            // client, admins included, and only the Admin SDK can see it.
            // `webhooks` is the same fixed-container trick as `lights` above — a
            // document needs an even number of path segments.
            webhookToken: (siteId, orderId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/private/webhooks/orders/${orderId}`;
            },
            // Abuse counters (F-RATE-01). `key` is a hash, never the raw email or IP
            // it counts — so this collection is a set of opaque tallies rather than a
            // log of who asked for a password reset. Server-only like its siblings;
            // `counters` is the fixed-container document that keeps the segment count
            // even.
            rateLimit: (siteId, key) => {
                verifySiteId(siteId);
                return `sites/${siteId}/private/ratelimit/counters/${key}`;
            }
        },
        mail: {
            collectionGroup: 'mail',
            doc: (siteId, mailId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/mail/${mailId}`;
            },
            collection: (siteId) => `sites/${siteId}/mail`
        },
        pages: {
            doc: (siteId, pageId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/pages/${pageId}`;
            },
            collection: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/pages`;
            }
        },
        events: {
            collectionGroup: 'events',
            doc: (siteId, eventId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/events/${eventId}`;
            },
            collection: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/events`;
            },
            regs: {
                doc: (siteId, eventId, subId) => {
                    verifySiteId(siteId);
                    return `sites/${siteId}/events/${eventId}/regs/${subId}`;
                },
                collection: (siteId, eventId) => {
                    verifySiteId(siteId);
                    return `sites/${siteId}/events/${eventId}/regs`;
                }
            },
            handlerforms: {
                doc: (siteId, eventId, formId) => {
                    verifySiteId(siteId);
                    return `sites/${siteId}/events/${eventId}/handlerforms/${formId}`;
                },
                collection: (siteId, eventId) => {
                    return `sites/${siteId}/events/${eventId}/handlerforms`;
                }
            },
            dogforms: {
                doc: (siteId, eventId, formId) => {
                    verifySiteId(siteId);
                    return `sites/${siteId}/events/${eventId}/dogforms/${formId}`;
                },
                collection: (siteId, eventId) => {
                    return `sites/${siteId}/events/${eventId}/dogforms`;
                }
            },
            equipageforms: {
                doc: (siteId, eventId, formId) => {
                    verifySiteId(siteId);
                    return `sites/${siteId}/events/${eventId}/equipageforms/${formId}`;
                },
                collection: (siteId, eventId) => {
                    return `sites/${siteId}/events/${eventId}/equipageforms`;
                }
            },
            messages: {
                doc: (siteId, eventId, messageId) => {
                    verifySiteId(siteId);
                    return `sites/${siteId}/events/${eventId}/messages/${messageId}`;
                },
                collection: (siteId, eventId) => {
                    verifySiteId(siteId);
                    return `sites/${siteId}/events/${eventId}/messages`;
                }
            },
            readStates: {
                doc: (siteId, eventId, uid) => {
                    verifySiteId(siteId);
                    verifyUid(uid);
                    return `sites/${siteId}/events/${eventId}/readStates/${uid}`;
                },
                collection: (siteId, eventId) => {
                    verifySiteId(siteId);
                    return `sites/${siteId}/events/${eventId}/readStates`;
                }
            },
            feedback: {
                doc: (siteId, eventId, token) => {
                    verifySiteId(siteId);
                    return `sites/${siteId}/events/${eventId}/feedback/${token}`;
                },
                collection: (siteId, eventId) => {
                    verifySiteId(siteId);
                    return `sites/${siteId}/events/${eventId}/feedback`;
                }
            }
        },
        punchcards: {
            doc: (siteId, cardId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/punchcards/${cardId}`;
            },
            collection: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/punchcards`;
            }
        },
        incidents: {
            doc: (siteId, incidentId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/incidents/${incidentId}`;
            },
            collection: (siteId) => {
                verifySiteId(siteId);
                return `sites/${siteId}/incidents`;
            }
        }
    },
    coaches: {
        doc: (uid) => {
            verifyUid(uid);
            return `coaches/${uid}`;
        },
        collection: 'coaches',
        invoices: {
            doc: (uid, invoiceId) => {
                verifyUid(uid);
                return `coaches/${uid}/invoices/${invoiceId}`;
            },
            collection: (uid) => {
                verifyUid(uid);
                return `coaches/${uid}/invoices`;
            }
        },
        config: {
            profile: (uid) => {
                verifyUid(uid);
                return `coaches/${uid}/config/profile`;
            },
            invoiceCounter: (uid) => {
                verifyUid(uid);
                return `coaches/${uid}/config/invoiceCounter`;
            }
        }
    }
};
/**
 * Number of path segments in a canonical site subcollection document:
 * `sites/{siteId}/{collection}/{docId}`.
 */
const SITE_SUBCOLLECTION_SEGMENTS = 4;
/**
 * Extract the siteId from a `sites/{siteId}/{collection}/{docId}` path, or
 * undefined when the path is not exactly that shape.
 *
 * Use this to filter the result of every `collectionGroup` query that is meant
 * to sweep site-level documents. A collection group matches EVERY collection
 * with the given leaf name at any depth, which includes the per-user mirrors
 * `sites/{siteId}/users/{uid}/orders` and `sites/{siteId}/users/{uid}/events`.
 * Those mirrors are full clones of the site document — same `meta.state`, same
 * `meta.reservedAt` — so a sweep that skips this check finds each order twice
 * and processes it twice.
 */
const siteIdFromSitePath = (path, collection) => {
    const segments = path.split('/');
    if (segments.length !== SITE_SUBCOLLECTION_SEGMENTS)
        return undefined;
    if (segments[0] !== 'sites' || segments[2] !== collection)
        return undefined;
    return segments[1];
};
export { FBPATHS, siteIdFromSitePath };
//# sourceMappingURL=paths.js.map